import { BapiProduct } from '@aboutyou/backbone';
import { Variant } from '@aboutyou/backbone/types/BapiProduct';
import { Parser } from 'expr-eval';
import { min } from 'modules/math';
import {
  BasketResponse,
  CartItem,
} from 'commerce/scayle/user-interaction/types';

const parseExpression = (condition: string, variables: any) => {
  try {
    const parser = new Parser();
    const expr = parser.parse(condition);
    return expr.evaluate(variables);
  } catch (error) {
    console.error({ condition, error });
  }
};

/**
 * TODO: add possibility to evaluate categories and attributes
 * Adjusts a condition string to make it evaluable for SCAYLE promotion's conditions.
 *
 * This function rewrites the condition by modifying the syntax of `size` and `filter` expressions
 * to include the SCAYLE evaluation logic. It replaces the original filter predicate with a call to
 * `evaluatePredicate`, while maintaining any logical operations (`&&` to `and`, `||` to `or`)
 * and any trailing conditions like `>= 1`.
 *
 * @param {string} condition - The condition string to be transformed. exm. `size(payload.items.filter(item, (item.product.id == 28172 && item.variant.id in [48357]))) >= 1`
 * @returns {string} - The adjusted condition string, ready for evaluation. exm `size(filter(evaluatePredicate("item.product.id == 28172 and item.variant.id in [48357]"), payload.items)) >= 1`
 */
const adjustCondition = (condition: string): string => {
  const sizeFilterRegexp =
    /size\(payload\.items\.filter\(item,?.\(([^()]*(?:\([^()]*\)[^()]*)*)\)\)\)?(.*)/g;

  const isFilter = condition.match(sizeFilterRegexp);

  if (isFilter?.length) {
    /**
     * Transforms the condition string by replacing the `filter` function and logical operators.
     *
     * @param {string} match - default param
     * Example: `size(payload.items.filter(item, (item.product.id == 28172 && item.variant.id in [48357]))) >= 1`
     * @param {string} p1 - The filter condition extracted from within the `filter()` function.
     * Example: `(item.product.id == 28172 && item.variant.id in [48357])`
     * @param {string} p2 - The trailing condition extracted after the `size()` function.
     * Example: `>= 1`
     * @returns {string} - The transformed condition string.
     */
    const replacer = (match: string, p1: string, p2: string): string => {
      return `size(filter(evaluatePredicate("${p1}"), payload.items))${p2}`;
    };

    condition = condition.replace(sizeFilterRegexp, replacer);
  }

  return condition.replace(/&&/g, 'and').replace(/\|\|/g, 'or');
};

export const evaluateCondition = (
  condition: string,
  cart: BasketResponse | null,
  product: BapiProduct,
  variant: Variant,
) => {
  // functions and objects which will replace corresponding ones in the string
  const variables = {
    size: (collection: any[]) => collection.length,
    payload: {
      ...cart,
      totals: cart?.cost,
    },
    evaluatePredicate: (predicate: string) => (item: CartItem) => {
      return parseExpression(predicate, { item });
    },
    withTaxPrices: cart?.items.map((item) => item.price.unit.withTax),
    math: {
      least: (numbers: number[]) => min(...numbers),
    },
    item: {
      product,
      variant,
      unitPrice: variant.price,
    },
  };

  const adjustedCondition = adjustCondition(condition);

  return parseExpression(adjustedCondition, variables);
};
